import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Modal } from "components/common";
import { downloadHsbcPackageFile, getHsbcPackageFile } from "redux/erp/paymentRequestSlice";

const TABS = [
  { kind: "pain", label: "XML v3 (pain.001)", needsFile: true,
    hint: "The payment file HSBC receives, built by the gateway." },
  { kind: "gpg", label: "Encrypted (.gpg)", needsFile: true,
    hint: "The XML v3 signed and encrypted with HSBC's key — this is what travels to the bank." },
  { kind: "instruction", label: "Portal instruction", needsFile: false,
    hint: "What the Portal sends the gateway (AxPaymentRequest). Viewable before the file is prepared." },
];
const SOURCE = {
  package: { text: "Prepared file — what HoF / BOD sign", tone: "tw-bg-[#ecfdf3] tw-text-[#067647]" },
  stale: { text: "Prepared before the payment changed — rebuild", tone: "tw-bg-[#fef3f2] tw-text-[#b42318]" },
  preview: { text: "Preview — built now from the request, not prepared yet", tone: "tw-bg-[#fffaeb] tw-text-[#b54708]" },
};

// Indents XML for reading; the downloaded file stays byte-for-byte what was prepared.
export const prettyXml = (xml) => {
  const text = String(xml || "").trim();
  if (!text.startsWith("<")) return text;
  let depth = 0;
  return text.replace(/>\s*</g, ">\n<").split("\n").map((raw) => {
    const node = raw.trim();
    if (/^<\//.test(node)) depth = Math.max(depth - 1, 0);
    const line = `${"  ".repeat(depth)}${node}`;
    const opens = /^<[^!?/][^>]*>$/.test(node) && !/\/>$/.test(node) && !/<\/[^>]+>$/.test(node);
    if (opens) depth += 1;
    return line;
  }).join("\n");
};

/** Shows the HSBC files as text — XML v3, the encrypted file, the Portal instruction — with copy and download. */
const HsbcFileViewer = ({ show, onHide, requestId, built, initialKind = "pain" }) => {
  const dispatch = useDispatch();
  const firstKind = built ? initialKind : "instruction";
  const [kind, setKind] = useState(firstKind);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const requested = useRef(new Set());

  // Each opening reads the files fresh (they change when the file is rebuilt).
  useEffect(() => {
    if (show) { setKind(firstKind); return; }
    setFiles({});
    requested.current = new Set();
  }, [show, firstKind]);

  useEffect(() => {
    if (!show || requested.current.has(kind)) return;
    requested.current.add(kind);
    setLoading(true);
    dispatch(getHsbcPackageFile({ id: requestId, kind })).unwrap()
      .then((res) => setFiles((all) => ({ ...all, [kind]: res?.data || null })))
      .catch(() => setFiles((all) => ({ ...all, [kind]: { error: true } })))
      .finally(() => setLoading(false));
  }, [show, kind, dispatch, requestId]);

  const file = files[kind];
  const tab = TABS.find((t) => t.kind === kind);
  const shown = file?.content ? (kind === "gpg" ? file.content : prettyXml(file.content)) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(file.content);
      toast.success("Copied.");
    } catch {
      toast.error("Could not copy — download the file instead.");
    }
  };
  const download = async () => {
    try {
      await downloadHsbcPackageFile(requestId, kind);
    } catch {
      toast.error("Could not download the file.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" title="HSBC payment file">
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div role="tablist" aria-label="HSBC files" className="tw-flex tw-flex-wrap tw-gap-1">
          {TABS.map((t) => {
            const disabled = t.needsFile && !built;
            return (
              <button key={t.kind} type="button" role="tab" aria-selected={kind === t.kind} disabled={disabled}
                title={disabled ? "Prepare the HSBC file first — the gateway makes it." : undefined}
                onClick={() => setKind(t.kind)}
                className={`tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-1 tw-text-[13px] ${
                  kind === t.kind ? "tw-border-geist-fg tw-bg-geist-fg tw-text-white"
                    : disabled ? "tw-cursor-not-allowed tw-border-geist-border tw-bg-transparent tw-text-geist-fg-3 tw-opacity-60"
                      : "tw-cursor-pointer tw-border-geist-border tw-bg-transparent tw-text-geist-fg-2 hover:tw-bg-geist-100"}`}>
                {t.label}
              </button>
            );
          })}
        </div>

        <p className="tw-m-0 tw-text-[12px] tw-text-geist-fg-3">{tab?.hint}</p>

        {file?.content && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-[12px]">
              {SOURCE[file.source] && <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-font-semibold ${SOURCE[file.source].tone}`}>{SOURCE[file.source].text}</span>}
              <span className="tw-text-geist-fg-3">{file.filename} · {file.bytes?.toLocaleString("en-US")} bytes</span>
              <code className="tw-text-geist-fg-3" title={file.sha256}>SHA-256 {file.sha256?.slice(0, 16)}…</code>
            </div>
            <div className="tw-flex tw-gap-2">
              <button type="button" onClick={copy}
                className="tw-cursor-pointer tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-white tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-semibold tw-text-geist-fg-2 hover:tw-bg-geist-100">
                <i className="bi bi-clipboard tw-mr-1" aria-hidden="true" />Copy
              </button>
              <button type="button" onClick={download}
                className="tw-cursor-pointer tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-accent tw-bg-geist-accent tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-semibold tw-text-white hover:tw-bg-geist-accent-2">
                <i className="bi bi-download tw-mr-1" aria-hidden="true" />Download
              </button>
            </div>
          </div>
        )}

        {(file?.problems || []).length > 0 && (
          <div role="alert" aria-label="Still missing" className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#fedf89] tw-bg-[#fffaeb] tw-px-3 tw-py-2 tw-text-[13px] tw-text-[#93370d]">
            <p className="tw-m-0 tw-font-semibold">Still missing before the file can be prepared:</p>
            <ul className="tw-m-0 tw-mt-1 tw-pl-5">
              {file.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <p className="tw-m-0 tw-mt-1 tw-text-[12px]">Placeholders in [brackets] show where they go in the XML.</p>
          </div>
        )}

        <div className="tw-min-h-[240px] tw-max-h-[60vh] tw-overflow-auto tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-[#0f172a]">
          {loading && !file ? (
            <p className="tw-m-0 tw-p-4 tw-text-[13px] tw-text-slate-300">Loading…</p>
          ) : file?.error ? (
            <p className="tw-m-0 tw-p-4 tw-text-[13px] tw-text-[#fda29b]">This file cannot be shown yet — see the review checklist.</p>
          ) : (
            <pre aria-label={`${tab?.label} content`} className="tw-m-0 tw-p-4 tw-text-[12px] tw-leading-relaxed tw-text-slate-100 tw-whitespace-pre tw-font-mono">{shown}</pre>
          )}
        </div>
      </div>
    </Modal>
  );
};

HsbcFileViewer.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  // Whether the gateway has prepared the file — XML v3 and .gpg exist only then.
  built: PropTypes.bool,
  initialKind: PropTypes.oneOf(["pain", "gpg", "instruction"]),
};

export default HsbcFileViewer;
